import { type FnBApplicableFor, FnBPropertyType } from "./fnb.types";

export type FnBPropertyTypeValue = FnBPropertyType | "";

export const FNB_PROPERTY_TYPE_OPTIONS: {
	value: FnBPropertyType;
	label: string;
}[] = [
	{ value: FnBPropertyType.RESTAURANT, label: "Restaurant" },
	{ value: FnBPropertyType.CABANA, label: "Cabana" },
	{ value: FnBPropertyType.RESTAURANT_CABANA, label: "Restaurant & Cabana" },
];

export interface FnBVisibilityContext {
	hasRestaurant?: boolean;
	hasCabana?: boolean;
	/** Create/edit: empty type still shows restaurant sections. */
	formEmptyTypeFallback?: boolean;
}

export function parseFnBPropertyType(raw: unknown): FnBPropertyTypeValue {
	const value = String(raw ?? "")
		.trim()
		.toUpperCase();
	if (value === FnBPropertyType.RESTAURANT) return FnBPropertyType.RESTAURANT;
	if (value === FnBPropertyType.CABANA) return FnBPropertyType.CABANA;
	if (value === FnBPropertyType.RESTAURANT_CABANA || value === "BOTH") {
		return FnBPropertyType.RESTAURANT_CABANA;
	}
	return "";
}

export function parseFnBApplicableFor(raw: unknown): FnBApplicableFor | "" {
	return parseFnBPropertyType(raw);
}

export function getFnBPropertyTypeLabel(type: FnBPropertyTypeValue): string {
	if (type === FnBPropertyType.RESTAURANT) return "Restaurant";
	if (type === FnBPropertyType.CABANA) return "Cabana";
	if (type === FnBPropertyType.RESTAURANT_CABANA) return "Restaurant & Cabana";
	return "";
}

/** Empty type is a valid listing state — never show a dash or placeholder. */
export function getFnBListingTypeLabel(type: FnBPropertyTypeValue): string {
	return getFnBPropertyTypeLabel(type);
}

/** Detail view uses an em dash when type is unset. */
export function getFnBDetailTypeLabel(type: FnBPropertyTypeValue): string {
	return getFnBPropertyTypeLabel(type) || "—";
}

export function getFnBApplicableLabel(value: FnBApplicableFor | ""): string {
	return getFnBPropertyTypeLabel(value) || "Not configured";
}

export function isCabanaApplicable(value: string): boolean {
	return (
		value === FnBPropertyType.CABANA ||
		value === FnBPropertyType.RESTAURANT_CABANA
	);
}

export function showsRestaurantGallery(
	type: FnBPropertyTypeValue,
	context: FnBVisibilityContext = {},
): boolean {
	if (type === FnBPropertyType.CABANA) return false;
	if (type) return true;
	if (context.formEmptyTypeFallback) return true;
	return Boolean(context.hasRestaurant);
}

export function showsCabanaGallery(
	type: FnBPropertyTypeValue,
	context: FnBVisibilityContext = {},
): boolean {
	if (type === FnBPropertyType.RESTAURANT) return false;
	if (
		type === FnBPropertyType.CABANA ||
		type === FnBPropertyType.RESTAURANT_CABANA
	) {
		return true;
	}
	return Boolean(context.hasCabana);
}

export function showsCuisineAndMenu(
	type: FnBPropertyTypeValue,
	context: FnBVisibilityContext = {},
): boolean {
	return showsRestaurantGallery(type, context);
}

export function usesFaqTabs(
	type: FnBPropertyTypeValue,
	context: FnBVisibilityContext = {},
): boolean {
	if (type === FnBPropertyType.RESTAURANT_CABANA) return true;
	if (type) return false;
	return Boolean(context.hasRestaurant && context.hasCabana);
}

export function defaultFaqApplicable(
	type: FnBPropertyTypeValue,
): FnBApplicableFor {
	return type === FnBPropertyType.CABANA
		? FnBPropertyType.CABANA
		: FnBPropertyType.RESTAURANT;
}

export function seatingApplicableOptions(
	type: FnBPropertyTypeValue,
	context: FnBVisibilityContext = {},
): Array<{ value: FnBApplicableFor; label: string }> {
	if (type === FnBPropertyType.CABANA) {
		return [{ value: FnBPropertyType.CABANA, label: "Cabana" }];
	}
	if (
		type === FnBPropertyType.RESTAURANT_CABANA ||
		(!type && context.hasCabana)
	) {
		return FNB_PROPERTY_TYPE_OPTIONS.map((option) => ({
			value: option.value,
			label: option.label,
		}));
	}
	return [{ value: FnBPropertyType.RESTAURANT, label: "Restaurant" }];
}

export function isApplicableValidForType(
	applicable: FnBApplicableFor | "",
	type: FnBPropertyTypeValue,
	context: FnBVisibilityContext = {},
): boolean {
	if (!applicable) return true;
	if (!type) return true;
	return seatingApplicableOptions(type, context).some(
		(option) => option.value === applicable,
	);
}

export function getFnBMediaSectionSubtitle(
	showRestaurant: boolean,
	showCabana: boolean,
): string {
	if (showRestaurant && showCabana) {
		return "Banner and gallery images for the restaurant and the cabana.";
	}
	if (showCabana) {
		return "Banner and gallery images for the cabana.";
	}
	return "Restaurant image gallery";
}

export function faqVisibleForPropertyType(
	applicable: FnBApplicableFor | "",
	type: FnBPropertyTypeValue,
	context: FnBVisibilityContext = {},
): boolean {
	if (!type || usesFaqTabs(type, context)) return true;
	return isApplicableValidForType(applicable, type, context);
}
